import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { v4 as uuid } from "uuid";
import { DataTableColumn } from "../types";
import { hasValue } from "../../../helper/hasValue";

export type FilterOperator = "=" | "!=" | ">=" | "<=" | ">" | "<";

export interface DateFilterValue {
  op: FilterOperator;
  date: Date | null;
}

export interface NumberFilterValue {
  op: FilterOperator;
  number: number | null;
}

export interface TimeFilterValue {
  op: FilterOperator;
  seconds: number | null;
}

export type BoolFilterValue = "true" | "false";

export type FilterValue =
  | string
  | boolean
  | number
  | Date
  | BoolFilterValue
  | DateFilterValue
  | TimeFilterValue
  | NumberFilterValue
  | null;

export function hasFilterValue(value: FilterValue): boolean {
  if (!hasValue(value)) return false;
  if (typeof value === "string" && value.trim().length === 0) return false;
  return true;
}

export interface ColumnFilter {
  id: string;
  columnId: string;
  value: FilterValue;
  operator: string;
}

export interface FilteringOptions<TRow> {
  columns: DataTableColumn<TRow>[];
  initial?: ColumnFilter[];
  onFilterChange?: (filter: ColumnFilter[]) => void;
  renderInput?: (
    column: DataTableColumn<TRow>,
    props: { value: any; onChange: (value: any) => void; onSubmit: () => void },
  ) => ReactNode | null;
  renderChipValue?: (
    column: DataTableColumn<TRow>,
    value: any,
    operator: string,
  ) => ReactNode | null | undefined;
  operatorOptions?: (
    column: DataTableColumn<TRow>,
  ) => { id: string; label: string; iconPath?: string }[] | null | undefined;
}

export interface FilteringState {
  filters: ColumnFilter[];
  setFilters: (filter: ColumnFilter[]) => void;
  update: (filter: ColumnFilter) => void;
  clear: () => void;
  clearColumn: (columnId: string) => void;
  hasActive: boolean;
  addFilter: (filter: ColumnFilter) => boolean;
  removeFilter: (filterId: string) => void;
  renderInput?: FilteringOptions<any>["renderInput"];
  renderChipValue?: FilteringOptions<any>["renderChipValue"];
  operatorOptions?: FilteringOptions<any>["operatorOptions"];
}

export function useFiltering<TRow>(
  options: FilteringOptions<TRow>,
): FilteringState {
  const {
    initial = [],
    onFilterChange,
    renderInput,
    renderChipValue,
    operatorOptions: operatorOptionsFn,
  } = options;

  const [filters, setFiltersState] = useState<ColumnFilter[]>(() =>
    initial.map((filter) => ({
      ...filter,
      id: filter.id || uuid(),
      operator: filter.operator || "=",
    })),
  );

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFilterChange?.(filters);
  }, [filters, onFilterChange]);

  const setFilters = useCallback((newFilters: ColumnFilter[]) => {
    setFiltersState(
      newFilters.map((filter) => ({
        ...filter,
        id: filter.id || uuid(),
        operator: filter.operator || "=",
      })),
    );
  }, []);

  const update = useCallback((filter: ColumnFilter) => {
    setFiltersState((current) =>
      current.map((currentFilter) =>
        currentFilter.id === filter.id ? filter : currentFilter,
      ),
    );
  }, []);

  const clear = useCallback(() => setFiltersState([]), []);

  const clearColumn = useCallback((columnId: string) => {
    setFiltersState((current) =>
      current.filter((filter) => filter.columnId !== columnId),
    );
  }, []);

  const hasActive = useMemo(
    () => filters.some((filter) => hasFilterValue(filter.value)),
    [filters],
  );

  const addFilter = useCallback((filter: ColumnFilter): boolean => {
    if (!hasFilterValue(filter.value)) return false;

    setFiltersState((current) => [
      ...current,
      {
        ...filter,
        id: filter.id || uuid(),
        operator: filter.operator || "=",
      },
    ]);
    return true;
  }, []);

  const removeFilter = useCallback((filterId: string) => {
    setFiltersState((current) =>
      current.filter((filter) => filter.id !== filterId),
    );
  }, []);

  return {
    filters,
    setFilters,
    update,
    clear,
    clearColumn,
    hasActive,
    addFilter,
    removeFilter,
    renderInput,
    renderChipValue,
    operatorOptions: operatorOptionsFn,
  };
}
